/**
 * @fileoverview AR-924 — Upload surface of the drawer: "Upload files" + "Add link" buttons, drop zone with
 * progress and the autosave hint. Rejected files toast and never leave the browser.
 */

import { useCallback } from 'react'
import { Box, Button, Group, Paper, Stack, Text, useMantineTheme } from '@mantine/core'
import { IconArrowBarUp, IconCloudUpload, IconInfoCircle, IconLink } from '@tabler/icons-react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { MAX_FILE_SIZE_MB, rejectFile } from '../../teacherUpload'
import { fileRejectionMessage, notifyError } from './feedback'
import { UploadProgressBar } from './UploadProgressBar'

const CLOUD_ICON_SIZE = 28
const BUTTON_ICON_SIZE = 16
const HINT_ICON_SIZE = 14
/** Figma: 6px between the cloud and its hint, and inside the autosave pill. */
const DROPZONE_CONTENT_GAP = 6
const HINT_PADDING_Y = 6

interface TeacherUploadAreaProps {
  readonly showDropzone: boolean
  readonly uploadPercent: number | null
  readonly disabled: boolean
  readonly onFiles: (files: File[]) => void
  readonly onAddLink: () => void
}

export function TeacherUploadArea(props: TeacherUploadAreaProps) {
  const { showDropzone, uploadPercent, disabled, onFiles, onAddLink } = props
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const tr = (key: string, options?: object) => t(`task_tracker.teacher_upload.${key}`, options)

  // No dropzone `accept`/`maxSize`: documents are allowed by extension and browsers disagree on their MIME
  // type, so `rejectFile` (the backend's rules) decides. Rejected files toast and never leave the browser.
  const onDrop = useCallback((dropped: File[]) => {
    const accepted: File[] = []
    dropped.forEach(file => {
      const reason = rejectFile(file)
      if (reason) notifyError(fileRejectionMessage(t, file.name, reason))
      else accepted.push(file)
    })
    if (accepted.length) onFiles(accepted)
  }, [onFiles, t])

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    disabled,
    noClick: true,
    noKeyboard: true,
  })
  const uploading = uploadPercent !== null

  return (
    <Stack spacing="md">
      <Group position="apart">
        <Button
          variant="light"
          color="gray"
          radius="lg"
          sx={theme => ({ color: theme.colors.dark[6] })}
          leftIcon={<IconArrowBarUp size={BUTTON_ICON_SIZE} />}
          onClick={open}
          disabled={disabled}
          loading={uploading}
        >
          {uploading ? tr('uploading', { percent: uploadPercent }) : tr('upload_files')}
        </Button>
        <Button
          variant="outline"
          leftIcon={<IconLink size={BUTTON_ICON_SIZE} />}
          onClick={onAddLink}
          disabled={disabled}
        >
          {tr('add_link')}
        </Button>
      </Group>

      {uploading && <UploadProgressBar percent={uploadPercent} />}

      <Box {...getRootProps()}>
        <input {...getInputProps()} />
        {showDropzone && (
          <Paper
            radius="md"
            py="md"
            px="md"
            sx={theme => {
              const brand = theme.fn.variant({ variant: 'light', color: theme.primaryColor })
              return {
                // Figma: a clearly visible lavender dash (brand ramp index 2), darker while dragging.
                border: `2px dashed ${isDragActive ? brand.color : theme.colors[theme.primaryColor][2]}`,
                background: brand.background,
                textAlign: 'center',
                cursor: disabled ? 'default' : 'pointer',
              }
            }}
            onClick={disabled ? undefined : open}
          >
            <Stack spacing={DROPZONE_CONTENT_GAP} align="center">
              <IconCloudUpload size={CLOUD_ICON_SIZE} color={theme.fn.primaryColor()} />
              <Text size="sm" color="dimmed">{tr('dropzone_hint', { mb: MAX_FILE_SIZE_MB })}</Text>
            </Stack>
          </Paper>
        )}
      </Box>

      {showDropzone && (
        <Paper withBorder radius="xl" px="md" py={HINT_PADDING_Y}>
          <Group spacing={DROPZONE_CONTENT_GAP} noWrap>
            <IconInfoCircle size={HINT_ICON_SIZE} style={{ flexShrink: 0 }} />
            <Text size="xs" color="dimmed">{tr('autosave_hint')}</Text>
          </Group>
        </Paper>
      )}
    </Stack>
  )
}
